<template>
  <div class="
    sw-model-fix-app
    sw-grid-flex
    sw-grid-flex--dir-row-reverse
    sw-grid-flex--dir-column--tab
    sw-grid-flex--wrap--tab
    sw--padding-hor-12 sw--padding-vert-8">
    <div class="sw-grid-flex__cell-2-3 sw-grid-flex__cell-1-1--tab sw--padding-left-5 sw--padding-left-0--tab">
      <div class="sw--height-full">
        <div class="sw-grid-flex--cell" role="tablist">
          <h1>Update your model</h1>
          <p>It looks like there was a design issue with your model <span class="italic">{{ modelName }}</span>. The areas of the model
            that need review are highlighted on the left. Please use this information and one of the options below to
            decide on how to proceed with this model. Note that the estimated ship date may change depending on what
            action is taken.</p>
          <!-- Upload -->
          <!-- Let us fix !-->
          <model-fix-let-us-fix
            v-if="letUsFixEnabled"
            :url="letUsFixUrl"
            @success="letUsFixSuccess"
            accordionId="accordion-1"
            accordionGroup="update-model"/>
          <!-- End of Let us fix !-->
          <accordion id="accordion-2" group="update-model">
            <!-- Upload -->
            <template v-slot:trigger>
                <span class="icon-models sw--margin-right-3 sw--font-size-20"></span>
                <span>Fix your model</span>
            </template>

            <template v-slot:target>
              <div class="sw--margin-bottom-7 sw--margin-left-7 sw--margin-left-0--tab">
                <template v-if="showUpload">
                  <div class="sw--margin-bottom-2 sw-dms--color-grey-46">
                    Repair the affected areas of the model yourself, and upload the new file using the area below. Our
                    system will process your file, then move the model into our review process. Once a new model is
                    uploaded, Print it Anyway will not be available for this model.
                  </div>
                  <model-fix-drag-and-drop-upload
                    :uploadAction="uploadAction"
                    :appId="appId"
                    :acceptedModelExtensions="acceptedModelExtensions"
                    :maxUploadSize="maxUploadSize"
                    :scaleMillimeters="scaleMillimeters"
                    :scaleMeters="scaleMeters"
                    :scaleInches="scaleInches"
                    :showDesignWithShapeways="showDesignWithShapeways"
                    :designWithShapewaysUrl="designWithShapewaysUrl"
                    :confirmBeforeUpload="confirmBeforeUpload"
                    @success="uploadSuccess"
                    @error="uploadError"/>
                  <div class="sw-model-material-drag-and-drop-upload__content" v-if="confirmErrorMessage">
                    <div class="sw-grid-flex__cell
                      sw-grid-flex
                      sw-grid-flex--align-center
                      sw--padding-4
                      sw--bg-alert-red-lightest
                      sw--border-radius-3
                      sw--position-relative
                      sw--margin-top-2">
                      <div class="icon-how-it-works sw--font-size-28 sw--margin-right-3 sw--text-red"></div>
                      <div v-html="confirmErrorMessage"></div>
                    </div>
                    <!--This spacer div is to make sure the upload element and the error are the same width-->
                    <div class="sw-model-material-drag-and-drop-upload__content-error-spacer"></div>
                  </div>
                </template>
                <template v-else-if="showAnalyzing">
                  <model-fix-analyzing-model/>
                </template>
                <template v-else-if="showConfirmUploaded">
                  <model-fix-confirm-uploaded-model
                    @success="confirmedUploadedModel"
                    @cancel="cancelUploadedModel"
                    @error="errorConfirmUploadedModel"
                    :confirmUrl="confirmUrl"/>
                </template>
              </div>
            </template>
          </accordion>

          <!-- PITA -->
          <model-fix-pita
            @success="pitaSuccess"
            :enabled="pitaEnabled"
            :url="pitaUrl"
            accordionId="accordion-3"
            accordionGroup="update-model"
          />

          <!-- Cancel -->
          <model-fix-cancel-order-items
            @success="canceledOrderItem"
            :url="cancelUrl"
            accordionId="accordion-4"
            accordionGroup="update-model"
          />
        </div>
      </div>
    </div>
    <div class="
      sw-grid-flex__cell
      sw--padding-right-5
      sw--padding-right-0--tab
      sw-model-fix-app-aside">
      <div v-if="showSlideshow">
        <model-fix-slideshow :modelName="modelName" :slides="slides"/>
      </div>
      <template v-else-if="showGrayRenderViz">
        <model-fix-gray-render-viz
          :grayRenderVizStatus="grayRenderVizStatus"
          :aoptPath="aoptPath"
          :glbPath="glbPath"
          :pictureUrl="pictureUrl"/>
        <model-fix-dimensions
          :model-dimensions="modelDimensions"
          :pricing-dimensions="pricingDimensions"/>
      </template>
    </div>
  </div>
</template>

<script>
  import ModelFixDragAndDropUpload
    from 'rrViewComponents/Model/Fix/ModelFixDragAndDropUploadComponent/model-fix-drag-and-drop-upload';
  import ModelFixAnalyzingModel
    from 'rrViewComponents/Model/Fix/ModelFixAnalyzingModelComponent/model-fix-analyzing-model';
  import ModelFixConfirmUploadedModel
    from 'rrViewComponents/Model/Fix/ModelFixConfirmUploadedModelComponent/model-fix-confirm-uploaded-model';
  import ModelFixGrayRenderViz, { GrayRenderVizStatus }
    from 'rrViewComponents/Model/Fix/ModelFixGrayRenderVizComponent/model-fix-gray-render-viz';
  import ModelFixDimensions from 'rrViewComponents/Model/Fix/ModelFixDimensionsComponent/model-fix-dimensions';
  import ModelFixSlideshow from 'rrViewComponents/Model/Fix/ModelFixSlideshowComponent/model-fix-slideshow';
  import ModelFixPita from 'rrViewComponents/Model/Fix/ModelFixPitaComponent/model-fix-pita';
  import ModelFixLetUsFix from 'rrViewComponents/Model/Fix/ModelFixLetUsFixComponent/model-fix-let-us-fix';
  import ModelFixCancelOrderItems
    from 'rrViewComponents/Model/Fix/ModelFixCancelOrderItemsComponent/model-fix-cancel-order-items';
  import Accordion from "rrViewComponents/Accordion/Accordion";
  import VueDataMapMixin from 'js/lib/vue/mixins/vue-data-map-mixin';
  import VueSwMixin from 'js/lib/vue/mixins/vue-sw-mixin';

  export const ModelFixStatus = Object.freeze({
    UPLOAD: 'upload',
    PROCESSING: 'processing',
    CONFIRM: 'confirm',
    RE_UPLOAD: 're-upload'
  });

  export default {
    name: 'model-fix-app',

    mixins: [VueDataMapMixin, VueSwMixin],

    components: {
      ModelFixDragAndDropUpload,
      ModelFixAnalyzingModel,
      ModelFixConfirmUploadedModel,
      ModelFixSlideshow,
      ModelFixGrayRenderViz,
      ModelFixDimensions,
      ModelFixPita,
      ModelFixCancelOrderItems,
      ModelFixLetUsFix,
      Accordion
    },

    props: {
      modelId: {
        type: Number,
        required: true
      },
      modelFileId: {
        type: Number,
        required: true
      },
      status: {
        type: String,
        default: ModelFixStatus.UPLOAD,
        validator: value => Object.values(ModelFixStatus).includes(value)
      },
      componentProps: {
        type: Object,
        required: true
      },
      pollInterval: {
        type: Number,
        default: 2000,
        validator: (value) => value >= 2000
      }
    },

    data: () => ({
      currentStatus: ModelFixStatus.UPLOAD,
      modelProcessingPollingUrl: null,
      confirmErrorMessage: null,

      // poll
      latestModelFileId: null, // of the last uploaded file

      // slideshow
      modelName: null,
      slides: [],

      // render
      grayRenderVizStatus: null,
      aoptPath: null,
      pictureUrl: null,

      // dimensions
      modelDimensions: null,
      pricingDimensions: null,

      // drag and drop
      uploadAction: null,
      appId: null,
      acceptedModelExtensions: null,
      maxUploadSize: null,
      scaleMillimeters: null,
      scaleMeters: null,
      scaleInches: null,
      showDesignWithShapeways: null,
      designWithShapewaysUrl: null,
      confirmBeforeUpload: null,

      // confirm model
      confirmUrl: null,

      // pita
      pitaEnabled: null,
      pitaUrl: null,

      //let us fix
      letUsFixEnabled: null,
      letUsFixUrl: null,

      // cancel order
      cancelUrl: null
    }),

    created() {
      this.currentStatus = this.status;
      this.mapChildrenObjectPropertiesToVueData(this.componentProps);

      if (this.currentStatus === ModelFixStatus.PROCESSING) {
        this.pollForModelProcessing();
      }

      if (this.currentStatus !== ModelFixStatus.CONFIRM) {
        this.mapObjectPropertiesToVueData({
          grayRenderVizStatus: GrayRenderVizStatus.PROCESSING,
          aoptPath: null,
          pictureUrl: null,
          modelDimensions: null,
          pricingDimensions: null
        });
      }
    },

    computed: {
      showUpload() {
        return [
          ModelFixStatus.UPLOAD,
          ModelFixStatus.RE_UPLOAD,
          ModelFixStatus.ERROR
        ].includes(this.currentStatus);
      },
      showAnalyzing() {
        return this.currentStatus === ModelFixStatus.PROCESSING;
      },
      showConfirmUploaded() {
        return this.currentStatus === ModelFixStatus.CONFIRM;
      },
      showSlideshow() {
        return [
          ModelFixStatus.UPLOAD,
          ModelFixStatus.RE_UPLOAD,
          ModelFixStatus.ERROR
        ].includes(this.currentStatus);
      },
      showGrayRenderViz() {
        return [
          ModelFixStatus.PROCESSING,
          ModelFixStatus.CONFIRM
        ].includes(this.currentStatus);
      },
      latestOrDefaultModelFileId() {
        return this.latestModelFileId || this.modelFileId
      }
    },

    methods: {
      uploadSuccess(response) {
        this.currentStatus = ModelFixStatus.PROCESSING;
        this.latestModelFileId = response.modelFileId;
        this.modelProcessingPollingUrl = response.modelProcessingPollingUrl;
        this.pitaEnabled = false;
        this.pollForModelProcessing();
        this.confirmErrorMessage = null;
      },
      uploadError() {
        this.currentStatus = ModelFixStatus.ERROR;
        this.confirmErrorMessage = null;
      },
      pollForModelProcessing() {
        if (this.modelProcessingPollingUrl) {
          this.$sw.polling(this.modelProcessingPollingUrl, this.doneModelProcessing, this.pollInterval);
        }
      },
      doneModelProcessing(data) {
        this.modelProcessingPollingUrl = null;
        this.mapObjectPropertiesToVueData({
          ...data['grayRenderViz'].data,
          modelDimensions: data['dimensions'].data.modelDimensions,
          pricingDimensions: data['dimensions'].data.pricingDimensions
        });

        this.currentStatus = ModelFixStatus.CONFIRM;
      },
      confirmedUploadedModel(data) {
        if (data.redirectUrl) {
          window.location.href = data.redirectUrl;
        }
      },
      cancelUploadedModel() {
        this.currentStatus = ModelFixStatus.RE_UPLOAD;

        this.mapObjectPropertiesToVueData({
          grayRenderVizStatus: GrayRenderVizStatus.PROCESSING,
          aoptPath: null,
          pictureUrl: null,
          modelDimensions: null,
          pricingDimensions: null
        });
      },
      errorConfirmUploadedModel(e) {
        this.cancelUploadedModel();

        this.currentStatus = ModelFixStatus.ERROR_TYPES;
        const responseText = JSON.parse(e.responseText);
        this.confirmErrorMessage = responseText.message;
      },
      pitaSuccess(data) {
        if (data.redirectUrl) {
          window.location.href = data.redirectUrl;
        }
      },
      letUsFixSuccess(data) {
          if (data.redirectUrl) {
              window.location.href = data.redirectUrl;
          }
      },
      canceledOrderItem(data) {
        if (data && data.redirectUrl) {
          window.location.href = data.redirectUrl;
        }
      }
    }
  }
</script>
